export interface PricingCalculatorInput {
  locationTier: LocationTier
  seniorityTier: SeniorityTier
  attractivenessTier: AttractivenessTier
  salaryTier: SalaryTier

  expectedOteSalary: number

  numPositions: number
  numMonths: number

  /** A ratio */
  expectedCvr: number
}

export interface PricingCalculatorResult {
  requiredApplicationsCreated: number
  requiredApplicationsSent: number
  requiredApplicationsAccepted: number
  requiredApplicationsAcceptedMonthly: number
  acceptedApplicationsToSell: number
  monthsOfHiring: number
  numberOfHires: number
  pricePerAcceptedApplication: number
  priceTotal: number
}

const PricingCalculatorConstants = {
  AcceptanceRate: 2.2,
  CsSentRate: 3.25,
  HyreCostPerApplication: 9.37,
  DontAcceptFrom: 4000,
  IncreaseFactor: 5,
  MinimumMonthlyPrice: 1500,
  MaximumMonthlyPrice: 5000,

  LocationDifficultyScoreWeight: 1.5,
  SeniorityDifficultyScoreWeight: 1,
  AttractivenessDifficultyScoreWeight: 1,
  SalaryDifficultyScoreWeight: 1.5,
} as const

export enum LocationTier {
  FullyRemote = 'lt-0-rem',
  Tier1Remote = 'lt-1-rem',
  Tier1Hybrid = 'lt-1-hyb',
  Tier1Onsite = 'lt-1-ons',
  Tier2Remote = 'lt-2-rem',
  Tier2Hybrid = 'lt-2-hyb',
  Tier2Onsite = 'lt-2-ons',
  Tier3Remote = 'lt-3-rem',
  Tier3Hybrid = 'lt-3-hyb',
  Tier3Onsite = 'lt-3-ons',
}

export enum SeniorityTier {
  CareerChange = 'st-cch',
  SdrBdr = 'st-sdr',
  MidLevel = 'st-mid',
  SeniorIc = 'st-sic',
  SeniorTeamLead = 'st-stl',
}

export enum SalaryTier {
  Low = 'sal-lo',
  Medium = 'sal-md',
  High = 'sal-hi',
}

export enum AttractivenessTier {
  Low = 'at-lo',
  Medium = 'at-md',
  High = 'at-hi',
}

export function calculateHyrePricing(input: PricingCalculatorInput): PricingCalculatorResult {
  const difficultyRating =
    getLocationTierDifficultyRating(input.locationTier) +
    getSeniorityDifficultyRating(input.seniorityTier) +
    getCompanyAttractivenessDifficultyRating(input.attractivenessTier) +
    getSalaryDifficultyRating(input.salaryTier)

  const pricePerAcceptedApplication = calculatePricePerApplication(difficultyRating)

  const totalRequiredApplicationsAccepted = input.numPositions * (1 / input.expectedCvr)

  const priceTotal = pricePerAcceptedApplication * totalRequiredApplicationsAccepted

  const minPrice = 4500
  const actualPrice = Math.max(priceTotal, minPrice)
  const acceptedApplicationsToSell = Math.ceil(actualPrice / pricePerAcceptedApplication)

  const totalRequiredApplicationsSent =
    acceptedApplicationsToSell * PricingCalculatorConstants.AcceptanceRate
  const totalRequiredApplicationsCreated =
    totalRequiredApplicationsSent * PricingCalculatorConstants.CsSentRate

  const monthsOfHiring = Math.ceil(input.numMonths)

  return {
    requiredApplicationsCreated: totalRequiredApplicationsCreated,
    requiredApplicationsSent: totalRequiredApplicationsSent,
    requiredApplicationsAccepted: Math.round(totalRequiredApplicationsAccepted),
    requiredApplicationsAcceptedMonthly: Math.round(
      totalRequiredApplicationsAccepted / monthsOfHiring,
    ),
    acceptedApplicationsToSell,
    pricePerAcceptedApplication,
    monthsOfHiring,
    numberOfHires: input.numPositions,

    priceTotal: actualPrice,
  }
}

export function calculateHeadhunterPricing(input: PricingCalculatorInput): PricingCalculatorResult {
  const priceTotal = input.expectedOteSalary * 0.25 * input.numPositions
  const pricePerAcceptedApplication = priceTotal * input.expectedCvr

  const totalRequiredApplicationsAccepted = input.numPositions * (1 / input.expectedCvr)
  const totalRequiredApplicationsSent =
    totalRequiredApplicationsAccepted * PricingCalculatorConstants.AcceptanceRate
  const totalRequiredApplicationsCreated = totalRequiredApplicationsSent * 1.75

  const monthsOfHiring = Math.ceil(input.numMonths * 1.2)
  return {
    requiredApplicationsCreated: totalRequiredApplicationsCreated,
    requiredApplicationsSent: totalRequiredApplicationsSent,
    requiredApplicationsAccepted: totalRequiredApplicationsAccepted,
    requiredApplicationsAcceptedMonthly: Math.round(
      totalRequiredApplicationsAccepted / monthsOfHiring,
    ),
    acceptedApplicationsToSell: totalRequiredApplicationsAccepted,
    monthsOfHiring,
    numberOfHires: input.numPositions,

    pricePerAcceptedApplication,
    priceTotal,
  }
}

// TODO maybe extract common calculations
export function calculateJobBoardPricing(input: PricingCalculatorInput): PricingCalculatorResult {
  const requiredApplicationsAccepted = input.numPositions * (1 / input.expectedCvr)
  const requiredApplicationsSent = requiredApplicationsAccepted * 25
  const requiredApplicationsCreated = requiredApplicationsSent

  const monthsOfHiring = Math.max(Math.ceil(input.numMonths * 2), 6)
  const priceTotal = monthsOfHiring * 1500

  const pricePerAcceptedApplication = priceTotal / requiredApplicationsAccepted

  return {
    requiredApplicationsCreated,
    requiredApplicationsSent,
    requiredApplicationsAccepted,
    requiredApplicationsAcceptedMonthly: Math.round(requiredApplicationsAccepted / monthsOfHiring),
    acceptedApplicationsToSell: requiredApplicationsAccepted,

    monthsOfHiring,
    numberOfHires: input.numPositions,

    pricePerAcceptedApplication,
    priceTotal,
  }
}

function getLocationTierDifficultyRating(locationTier: LocationTier) {
  const score = {
    [LocationTier.FullyRemote]: 0.1,
    [LocationTier.Tier1Remote]: 0.2,
    [LocationTier.Tier1Hybrid]: 0.5,
    [LocationTier.Tier1Onsite]: 0.7,
    [LocationTier.Tier2Remote]: 0.5,
    [LocationTier.Tier2Hybrid]: 0.7,
    [LocationTier.Tier2Onsite]: 1.0,
    [LocationTier.Tier3Remote]: 1.0,
    [LocationTier.Tier3Hybrid]: 1.3,
    [LocationTier.Tier3Onsite]: 1.5,
  }[locationTier]

  return score * PricingCalculatorConstants.LocationDifficultyScoreWeight
}

function getSeniorityDifficultyRating(seniority: SeniorityTier) {
  const score = {
    [SeniorityTier.CareerChange]: 0.3,
    [SeniorityTier.SdrBdr]: 1.0,
    [SeniorityTier.MidLevel]: 0.5,
    [SeniorityTier.SeniorIc]: 1.0,
    [SeniorityTier.SeniorTeamLead]: 1.3,
  }[seniority]

  return score * PricingCalculatorConstants.SeniorityDifficultyScoreWeight
}

function getCompanyAttractivenessDifficultyRating(attractiveness: AttractivenessTier) {
  const score = {
    [AttractivenessTier.Low]: 1.0,
    [AttractivenessTier.Medium]: 0.75,
    [AttractivenessTier.High]: 0.3,
  }[attractiveness]

  return score * PricingCalculatorConstants.AttractivenessDifficultyScoreWeight
}

function getSalaryDifficultyRating(salary: SalaryTier) {
  const score = {
    [SalaryTier.Low]: 1.0,
    [SalaryTier.Medium]: 0.75,
    [SalaryTier.High]: 0.3,
  }[salary]

  return score * PricingCalculatorConstants.SalaryDifficultyScoreWeight
}

function calculatePricePerApplication(score: number): number {
  if (score <= 1.2) return 100
  else if (score <= 1.5) return 125
  else if (score <= 2.2) return 150
  else if (score < 2.5) return 200
  else if (score < 3) return 250
  else if (score < 3.5) return 300
  else if (score < 4) return 350
  else if (score < 4.5) return 400
  else if (score < 5) return 450
  else return 500
}
